import { AlertTriangle, RefreshCw, RotateCcw } from 'lucide-react';
import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
	children: ReactNode;
	/** Fallback UI to show when there's an error */
	fallback?: ReactNode;
	/** Whether to show detailed error information */
	showDetails?: boolean;
	/** Callback when an error occurs */
	onError?: (error: Error, errorInfo: ErrorInfo) => void;
	/** Whether to automatically retry after errors */
	enableAutoRetry?: boolean;
	/** Auto retry delay in milliseconds */
	retryDelay?: number;
}

interface State {
	hasError: boolean;
	error: Error | null;
	errorInfo: ErrorInfo | null;
	retryCount: number;
}

/**
 * Error boundary component that catches JavaScript errors anywhere in the child
 * component tree, logs those errors, and displays a fallback UI.
 *
 * Features:
 * - Catches and contains React errors
 * - Provides user-friendly error messages
 * - Offers retry functionality
 * - Logs errors for debugging
 * - Prevents entire app crashes
 */
export class ErrorBoundary extends Component<Props, State> {
	private retryTimeout: NodeJS.Timeout | null = null;

	constructor(props: Props) {
		super(props);
		this.state = {
			hasError: false,
			error: null,
			errorInfo: null,
			retryCount: 0,
		};
	}

	static getDerivedStateFromError(error: Error): Partial<State> {
		// Update state so the next render will show the fallback UI
		return {
			hasError: true,
			error,
		};
	}

	componentDidCatch(error: Error, errorInfo: ErrorInfo) {
		// Log the error
		console.error('ErrorBoundary caught an error:', error, errorInfo);

		this.setState({
			error,
			errorInfo,
		});

		// Call the onError callback if provided
		if (this.props.onError) {
			this.props.onError(error, errorInfo);
		}

		// Auto retry if enabled and retry count is low
		if (this.props.enableAutoRetry && this.state.retryCount < 3) {
			const delay = this.props.retryDelay || 2000;
			this.retryTimeout = setTimeout(() => {
				this.handleRetry();
			}, delay);
		}

		// Report to error tracking service (if available)
		if (typeof window !== 'undefined' && 'Sentry' in window) {
			(window as any).Sentry.captureException(error, {
				contexts: {
					react: {
						componentStack: errorInfo.componentStack,
					},
				},
			});
		}
	}

	componentWillUnmount() {
		if (this.retryTimeout) {
			clearTimeout(this.retryTimeout);
		}
	}

	handleRetry = () => {
		this.setState(prevState => ({
			hasError: false,
			error: null,
			errorInfo: null,
			retryCount: prevState.retryCount + 1,
		}));
	};

	handleReload = () => {
		window.location.reload();
	};

	render() {
		if (this.state.hasError) {
			// Custom fallback UI
			if (this.props.fallback) {
				return this.props.fallback;
			}

			// Default error UI
			return (
				<div className="min-h-[200px] flex items-center justify-center p-6 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
					<div className="max-w-md text-center">
						<div className="flex justify-center mb-4">
							<AlertTriangle className="w-12 h-12 text-red-600 dark:text-red-400" />
						</div>

						<h3 className="mb-2 text-lg font-semibold text-red-900 dark:text-red-100">
							Something went wrong
						</h3>

						<p className="mb-4 text-sm text-red-700 dark:text-red-300">
							{this.state.error?.message || 'An unexpected error occurred'}
						</p>

						{/* Error Details (if enabled) */}
						{this.props.showDetails && this.state.errorInfo && (
							<details className="mb-4 text-left">
								<summary className="text-xs text-red-600 cursor-pointer dark:text-red-400 hover:underline">
									Show technical details
								</summary>
								<div className="overflow-auto p-3 mt-2 max-h-32 font-mono text-xs text-red-800 bg-red-100 rounded dark:bg-red-900/30 dark:text-red-200">
									<div className="mb-2">
										<strong>Error:</strong> {this.state.error?.toString()}
									</div>
									<div>
										<strong>Stack:</strong>
										<pre className="text-xs whitespace-pre-wrap">
											{this.state.errorInfo.componentStack}
										</pre>
									</div>
								</div>
							</details>
						)}

						{/* Retry Count */}
						{this.state.retryCount > 0 && (
							<p className="mb-4 text-xs text-red-600 dark:text-red-400">
								Retry attempts: {this.state.retryCount}/3
							</p>
						)}

						{/* Action Buttons */}
						<div className="flex gap-3 justify-center">
							<button
								onClick={this.handleRetry}
								className="flex gap-2 items-center px-4 py-2 text-sm font-medium text-red-700 bg-red-100 rounded-lg transition-colors hover:bg-red-200 dark:text-red-200 dark:bg-red-900/30 dark:hover:bg-red-900/50"
							>
								<RotateCcw className="w-4 h-4" />
								Try Again
							</button>

							<button
								onClick={this.handleReload}
								className="flex gap-2 items-center px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg transition-colors hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-600"
							>
								<RefreshCw className="w-4 h-4" />
								Reload Page
							</button>
						</div>

						{/* Prevention Tips */}
						<div className="p-3 mt-4 bg-yellow-50 rounded border border-yellow-200 dark:bg-yellow-900/20 dark:border-yellow-800">
							<h4 className="mb-1 text-xs font-semibold text-yellow-800 dark:text-yellow-200">
								💡 To prevent crashes:
							</h4>
							<ul className="space-y-1 text-xs text-left text-yellow-700 dark:text-yellow-300">
								<li>• Avoid loading very large datasets at once</li>
								<li>• Use pagination for long lists</li>
								<li>• Close unused browser tabs</li>
								<li>• Refresh the page periodically</li>
							</ul>
						</div>
					</div>
				</div>
			);
		}

		return this.props.children;
	}
}

/**
 * HOC wrapper for easier usage
 */
export function withErrorBoundary<P extends object>(
	Component: React.ComponentType<P>,
	errorBoundaryProps?: Omit<Props, 'children'>
) {
	const WrappedComponent = (props: P) => (
		<ErrorBoundary {...errorBoundaryProps}>
			<Component {...props} />
		</ErrorBoundary>
	);

	WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;

	return WrappedComponent;
}
